import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "antd";
import { FiExternalLink } from "react-icons/fi";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/he";
import { getOrdersByEmail, getLDSOrders } from "../../../services/fhpService";
import { performPostFetch } from "../../../services/fetchService";
import { resolveApiUrl } from "../../../assets/GlobalVariables";
import { getUserInfo } from "../../../services/localStorage";
import { isAdminUser, getUserDetails } from "../../../services/authService";
import { useAgentEntitlements } from "../../../features/agentEntitlements/useAgentEntitlements";
import { HiOutlineChevronRight, HiOutlineChevronLeft, HiOutlineCalendar } from "react-icons/hi2";
import {
  deriveLdsOrderBusinessStatus,
  getLdsOrderBusinessStatusLabelHe,
  isLdsOrderBusinessSettled,
} from "../../../utils/ldsOrderStatus";
import { ordersDashboardUrlFromSearchParams } from "../../../utils/ordersDashboardRoutes";
import { resolveRecentOrderStayDates } from "../../../features/orders/visibility/orderDateDisplaySsot";
import {
  RECENT_ORDERS_HOME_FEED_ENDPOINT,
  buildRecentOrdersHomeFeedHeaders,
  parseRecentOrdersHomeFeedResponse,
  resolveHomeFeedSessionId,
} from "../../../utils/recentOrdersHomeFeed";
import "./OrdersCalendarCard.scss";

dayjs.extend(customParseFormat);
dayjs.locale("he");

/** Same endpoint as RecentOrdersCard / DashboardPage — system-wide for any active session. */
const CALENDAR_RECENT_FEED_LIMIT = 200;
/** Agent secondary sources (own hotels / LDS) — keep bounded so the tab stays responsive. */
const SOURCE_TIMEOUT_MS = 25000;
/** Admin home-feed SSOT — BE can enrich thumbnails for up to 200 rows. */
const ADMIN_FEED_TIMEOUT_MS = 35000;
/** Hard UI budget — always drop the loading banner. */
const LOADING_MAX_MS = 12000;

const SOURCE_LABEL_HE = {
  recent: "הזמנות אחרונות",
  hotels: "מלונות",
  packages: "חבילות",
  tours: "טיולים",
};

const formatSourceWarningsHe = (keys) =>
  keys.map((k) => SOURCE_LABEL_HE[k] || k).join(", ");

const withTimeout = (promise, ms, label) => {
  let timerId;
  const timeoutPromise = new Promise((_, reject) => {
    timerId = window.setTimeout(() => {
      reject(new Error(`[OrdersCalendarCard] ${label} timed out after ${ms}ms`));
    }, ms);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timerId) window.clearTimeout(timerId);
  });
};

const parseDate = (dateString) => {
  if (!dateString) return null;
  try {
    const raw = String(dateString).trim();
    if (!raw) return null;

    const dayFirst = dayjs(raw, "DD/MM/YYYY", true);
    if (dayFirst.isValid()) return dayFirst.startOf("day");

    const dayFirstTime = dayjs(raw, "DD/MM/YYYY HH:mm:ss", true);
    if (dayFirstTime.isValid()) return dayFirstTime.startOf("day");

    if (raw.includes("/")) {
      const parts = raw.split("/");
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        if (day && month && year) {
          const rebuilt = dayjs(
            `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`
          );
          if (rebuilt.isValid()) return rebuilt.startOf("day");
        }
      }
    }

    const iso = dayjs(raw);
    if (iso.isValid()) return iso.startOf("day");
    return null;
  } catch (e) {
    return null;
  }
};

const emptyOrdersEnvelope = { data: { orders: [] } };

const orderKey = (order) => `${order.type || "unknown"}:${order.id || ""}`;

/** Prefer HTTP Date (server clock) when local clock is skewed by >14 days. */
const resolveCalendarToday = (serverDateHeader) => {
  const local = dayjs();
  if (!serverDateHeader) return local;
  const server = dayjs(serverDateHeader);
  if (!server.isValid()) return local;
  if (Math.abs(local.diff(server, "day")) > 14) {
    console.warn("[OrdersCalendarCard] local clock skew vs server Date", {
      local: local.format("YYYY-MM-DD"),
      server: server.format("YYYY-MM-DD"),
      header: serverDateHeader,
    });
    return server;
  }
  return local;
};

const mapHotelOrder = (order) => {
  const checkin = order.checkin ?? order.checkIn ?? order.check_in;
  const checkout = order.checkout ?? order.checkOut ?? order.check_out;
  return {
    id: order.id ?? order.hotelorderid ?? order.hr_order_number,
    type: "hotel",
    typeLabel: "מלון",
    title: order.hotelname || order.hotelName || order.title || "מלון",
    clientName: order.fullname || order.customer_name || order.main_contact || "",
    checkin,
    checkout,
    date: parseDate(checkin),
    checkoutDate: parseDate(checkout),
    paid: order.paid,
  };
};

const mapRecentFeedOrder = (order) => {
  if (!order || order.cancelled) return null;
  const stay = resolveRecentOrderStayDates(order);
  const checkin = stay.checkIn || order.checkIn || order.checkin || order.dep_date;
  const checkout = stay.checkOut || order.checkOut || order.checkout || order.ret_date;
  if (!checkin && !checkout) return null;

  const rawType = String(order.type || order.productType || "").toLowerCase();
  let type = "hotel";
  let typeLabel = "מלון";
  if (rawType.includes("organized") || rawType.includes("tour")) {
    type = "tour";
    typeLabel = "טיול";
  } else if (rawType.includes("package") || rawType.includes("flight")) {
    type = "package";
    typeLabel = "חבילה";
  }

  return {
    id: order.id || order.dealid || order.hr_order_number || order.orderId,
    type,
    typeLabel,
    title:
      order.title ||
      order.hotelname ||
      order.hotelName ||
      order.tour_name ||
      typeLabel,
    clientName: order.mainContact || order.main_contact || order.fullname || "",
    checkin,
    checkout,
    date: parseDate(checkin),
    checkoutDate: parseDate(checkout),
    paid: order.paid,
    rawLdsOrder: type === "hotel" ? null : order,
  };
};

const mergeCalendarOrders = (...lists) => {
  const byKey = new Map();
  lists.flat().forEach((order) => {
    if (!order?.id && !order?.checkin) return;
    const key = orderKey(order);
    if (!byKey.has(key)) byKey.set(key, order);
  });
  return Array.from(byKey.values());
};

const orderTouchesMonth = (order, month) => {
  const checkIn = order.date || parseDate(order.checkin);
  const checkOut = order.checkoutDate || parseDate(order.checkout);
  return Boolean(
    (checkIn && checkIn.isSame(month, "month")) ||
      (checkOut && checkOut.isSame(month, "month"))
  );
};

/** If the open month has no events, jump to the nearest month that does (fixes wrong-year empty grids). */
const pickBestMonthWithEvents = (orders, preferredToday) => {
  const preferred = preferredToday.startOf("month");
  const monthKeys = new Set();
  orders.forEach((order) => {
    const checkIn = order.date || parseDate(order.checkin);
    const checkOut = order.checkoutDate || parseDate(order.checkout);
    if (checkIn) monthKeys.add(checkIn.format("YYYY-MM"));
    if (checkOut) monthKeys.add(checkOut.format("YYYY-MM"));
  });
  if (monthKeys.size === 0) return preferred;

  const preferredKey = preferred.format("YYYY-MM");
  if (monthKeys.has(preferredKey)) return preferred;

  // Same calendar month in another year (e.g. local 2024 vs product 2026).
  const sameMonthOtherYear = Array.from(monthKeys)
    .map((key) => dayjs(`${key}-01`))
    .filter((d) => d.isValid() && d.month() === preferred.month())
    .sort((a, b) => Math.abs(a.diff(preferred)) - Math.abs(b.diff(preferred)));
  if (sameMonthOtherYear.length > 0) return sameMonthOtherYear[0].startOf("month");

  const nearest = Array.from(monthKeys)
    .map((key) => dayjs(`${key}-01`))
    .filter((d) => d.isValid())
    .sort((a, b) => Math.abs(a.diff(preferred)) - Math.abs(b.diff(preferred)));
  return nearest[0]?.startOf("month") || preferred;
};

const OrdersCalendarCard = ({
  readyToFetch = true,
  deferLoad = false,
  deferMs = 0,
  /** Same /getRecentOrdersAll rows as RecentOrdersCard — primary chip source. */
  ordersFromDashboard = null,
}) => {
  const navigate = useNavigate();
  const hasDashboardSeed =
    Array.isArray(ordersFromDashboard) && ordersFromDashboard.length > 0;
  const [allOrders, setAllOrders] = useState([]);
  // Grid always mounts; banner only — never gate the month UI on fetch.
  const [isLoading, setIsLoading] = useState(() => !hasDashboardSeed);
  const [loadError, setLoadError] = useState(null);
  const [fetchWarnings, setFetchWarnings] = useState([]);
  const [todayAnchor, setTodayAnchor] = useState(() => dayjs());
  const [currentMonth, setCurrentMonth] = useState(() => dayjs().startOf("month"));
  const [selectedDate, setSelectedDate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const monthAutoCorrectedRef = useRef(false);
  const agentEntitlements = useAgentEntitlements();

  const applyMonthIfNeeded = (orders, serverDateHeader) => {
    const resolvedToday = resolveCalendarToday(serverDateHeader);
    setTodayAnchor(resolvedToday);
    if (monthAutoCorrectedRef.current) return resolvedToday;
    const preferredMonth = resolvedToday.startOf("month");
    const bestMonth = pickBestMonthWithEvents(orders, resolvedToday);
    monthAutoCorrectedRef.current = true;
    setCurrentMonth(bestMonth);
    if (!bestMonth.isSame(preferredMonth, "month")) {
      console.info("[OrdersCalendarCard] auto-corrected month", {
        fromPreferred: preferredMonth.format("YYYY-MM"),
        to: bestMonth.format("YYYY-MM"),
        todayLocal: dayjs().format("YYYY-MM-DD"),
        todayResolved: resolvedToday.format("YYYY-MM-DD"),
        serverDateHeader,
        orderCount: orders.length,
      });
    }
    return resolvedToday;
  };

  // Seed chips immediately from the dashboard RecentOrders feed (parity with that tab).
  useEffect(() => {
    if (!Array.isArray(ordersFromDashboard) || ordersFromDashboard.length === 0) {
      return;
    }
    try {
      const mapped = ordersFromDashboard.map(mapRecentFeedOrder).filter(Boolean);
      if (mapped.length === 0) {
        setIsLoading(false);
        return;
      }
      setAllOrders((prev) => mergeCalendarOrders(mapped, prev));
      applyMonthIfNeeded(mapped, null);
      setIsLoading(false);
    } catch (err) {
      console.warn("[OrdersCalendarCard] dashboard seed failed", err);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed when dashboard feed arrives
  }, [ordersFromDashboard]);

  // Fetch calendar sources — never block the grid; each source has its own timeout.
  useEffect(() => {
    if (!readyToFetch) {
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;
    let deferTimer;
    let safetyTimer;
    const warnings = [];

    const markDoneLoading = () => {
      if (!cancelled) setIsLoading(false);
    };

    /** Same auth + parse path as RecentOrdersCard / DashboardPage home feed. */
    const fetchRecentHomeFeed = async (timeoutMs) => {
      const sessionId = resolveHomeFeedSessionId();
      const payload = { limit: CALENDAR_RECENT_FEED_LIMIT };
      if (sessionId) {
        payload.sessionID = sessionId;
      }
      const res = await withTimeout(
        performPostFetch(
          resolveApiUrl(RECENT_ORDERS_HOME_FEED_ENDPOINT),
          payload,
          false,
          { headers: buildRecentOrdersHomeFeedHeaders() }
        ),
        timeoutMs,
        "recent-feed"
      );
      const parsed = parseRecentOrdersHomeFeedResponse(res);
      if (!parsed.ok) {
        throw new Error(parsed.error || "recent_feed_not_ok");
      }
      return { orders: parsed.orders, serverDateHeader: res?.headers?.date || null };
    };

    const fetchAllUpcomingOrders = async () => {
      const userInfo = getUserInfo();
      if (!userInfo?.email || !userInfo?.id) {
        if (!cancelled) {
          if (!hasDashboardSeed) setAllOrders([]);
          setIsLoading(false);
          setLoadError("לא מחוברים — לא ניתן לטעון את היומן");
        }
        return;
      }

      const isAdmin = isAdminUser();
      const ldsOrdersGuardCtx = {
        selectors: {
          canSearchFlights: agentEntitlements.canSearchFlights,
          canSearchPackages: agentEntitlements.canSearchPackages,
          canUseDynamicPackages: agentEntitlements.canUseDynamicPackages,
          canOrganizedTours: agentEntitlements.canOrganizedTours,
          canSearchHotels: agentEntitlements.canSearchHotels,
        },
        entitlementsStatus: agentEntitlements.status,
        userId: userInfo.id ?? null,
        role: String(getUserDetails()?.role ?? "").trim() || null,
        adminBypass: isAdmin,
      };

      let serverDateHeader = null;

      try {
        if (!cancelled) {
          setLoadError(null);
          setFetchWarnings([]);
          if (!hasDashboardSeed) setIsLoading(true);
        }

        const agentEmail = userInfo.email;
        const agentId = userInfo.id;
        const fromDashboardProp = Array.isArray(ordersFromDashboard)
          ? ordersFromDashboard.map(mapRecentFeedOrder).filter(Boolean)
          : [];

        // Primary SSOT: /getRecentOrdersAll (system-wide for admin + agents).
        let fromRecent = [];
        try {
          const feed = await fetchRecentHomeFeed(
            isAdmin ? ADMIN_FEED_TIMEOUT_MS : SOURCE_TIMEOUT_MS
          );
          if (cancelled) return;
          serverDateHeader = feed.serverDateHeader || serverDateHeader;
          fromRecent = (feed.orders || []).map(mapRecentFeedOrder).filter(Boolean);
        } catch (err) {
          console.warn("[OrdersCalendarCard] recent feed fetch failed", err);
          // Dashboard seed already proved the endpoint works — don't scare the admin.
          if (!hasDashboardSeed && fromDashboardProp.length === 0) {
            warnings.push("recent");
          }
        }

        const earlyMerged = mergeCalendarOrders(fromDashboardProp, fromRecent);
        if (earlyMerged.length > 0) {
          setAllOrders((prev) => mergeCalendarOrders(earlyMerged, prev));
          applyMonthIfNeeded(earlyMerged, serverDateHeader);
        }
        markDoneLoading();

        // Admin: home feed is the calendar SSOT (all agents). Skip flaky admin
        // hotel-orders + LDS scope=all bulk calls that were timing out at 10s.
        if (isAdmin) {
          const merged = earlyMerged;
          if (!cancelled) {
            setAllOrders(merged);
            setFetchWarnings(
              merged.length > 0 ? [] : [...warnings]
            );
            applyMonthIfNeeded(merged, serverDateHeader);
            console.info("[OrdersCalendarCard] loaded (admin home-feed SSOT)", {
              recentFeed: fromRecent.length,
              dashboardProp: fromDashboardProp.length,
              merged: merged.length,
              warnings,
              serverDateHeader,
              todayResolved: resolveCalendarToday(serverDateHeader).format(
                "YYYY-MM-DD"
              ),
            });
            if (merged.length === 0 && warnings.length > 0) {
              setLoadError(
                `לא הצלחנו לטעון הזמנות ליומן (${formatSourceWarningsHe(warnings)}).`
              );
            } else {
              setLoadError(null);
            }
          }
          return;
        }

        // Agent: enrich with own hotels + LDS packages/tours (scope=mine).
        const hotelsPromise = withTimeout(
          getOrdersByEmail({ email: agentEmail }).then((res) => {
            serverDateHeader = res?.headers?.date || serverDateHeader;
            return res;
          }),
          SOURCE_TIMEOUT_MS,
          "hotels"
        ).catch((err) => {
          console.warn("[OrdersCalendarCard] hotels fetch failed", err);
          warnings.push("hotels");
          return emptyOrdersEnvelope;
        });

        const packagesPromise = withTimeout(
          getLDSOrders(agentId, "FlightsHotel", 1, 100, {}, ldsOrdersGuardCtx),
          SOURCE_TIMEOUT_MS,
          "packages"
        ).catch((err) => {
          console.warn("[OrdersCalendarCard] packages fetch failed", err);
          warnings.push("packages");
          return emptyOrdersEnvelope;
        });

        const toursPromise = withTimeout(
          getLDSOrders(agentId, "OrganizedTour", 1, 100, {}, ldsOrdersGuardCtx),
          SOURCE_TIMEOUT_MS,
          "tours"
        ).catch((err) => {
          console.warn("[OrdersCalendarCard] tours fetch failed", err);
          warnings.push("tours");
          return emptyOrdersEnvelope;
        });

        const [hotelsData, packagesData, toursData] = await Promise.all([
          hotelsPromise,
          packagesPromise,
          toursPromise,
        ]);
        if (cancelled) return;

        const hotels =
          hotelsData?.data?.orders
            ?.filter((o) => !o.cancelled && (o.checkin || o.checkIn || o.check_in))
            .map(mapHotelOrder) || [];

        const packages =
          packagesData?.data?.orders
            ?.filter((o) => !o.cancelled && o.dep_date)
            .map((order) => ({
              id: order.dealid || order.id,
              type: "package",
              typeLabel: "חבילה",
              title:
                order?.order_response?.reservation?.hotel?.hotelName ||
                "חבילת נופש",
              clientName: order.main_contact || "",
              checkin: order.dep_date,
              checkout: order.ret_date,
              date: parseDate(order.dep_date),
              checkoutDate: parseDate(order.ret_date),
              paid: order.paid,
              rawLdsOrder: order,
            })) || [];

        const tours =
          toursData?.data?.orders
            ?.filter((o) => !o.cancelled && o.dep_date)
            .map((order) => ({
              id: order.dealid || order.id,
              type: "tour",
              typeLabel: "טיול",
              title: order.tour_name || "טיול מאורגן",
              clientName: order.main_contact || "",
              checkin: order.dep_date,
              checkout: order.ret_date,
              date: parseDate(order.dep_date),
              checkoutDate: parseDate(order.ret_date),
              paid: order.paid,
              rawLdsOrder: order,
            })) || [];

        const merged = mergeCalendarOrders(
          fromDashboardProp,
          fromRecent,
          hotels,
          packages,
          tours
        );

        // If any path produced events, don't show a partial-failure banner.
        const visibleWarnings = merged.length > 0 ? [] : warnings;

        setAllOrders(merged);
        setFetchWarnings([...visibleWarnings]);
        applyMonthIfNeeded(merged, serverDateHeader);

        console.info("[OrdersCalendarCard] loaded", {
          hotels: hotels.length,
          packages: packages.length,
          tours: tours.length,
          recentFeed: fromRecent.length,
          dashboardProp: fromDashboardProp.length,
          merged: merged.length,
          warnings,
          visibleWarnings,
          serverDateHeader,
          todayResolved: resolveCalendarToday(serverDateHeader).format(
            "YYYY-MM-DD"
          ),
        });

        if (merged.length === 0 && warnings.length > 0) {
          setLoadError(
            `לא הצלחנו לטעון הזמנות ליומן (${formatSourceWarningsHe(warnings)}).`
          );
        } else {
          setLoadError(null);
        }
      } catch (error) {
        console.error("[OrdersCalendarCard] Error fetching upcoming orders:", error);
        if (!cancelled) {
          setLoadError("לא הצלחנו לטעון את היומן");
        }
      } finally {
        markDoneLoading();
      }
    };

    const runFetch = () => {
      if (cancelled) return;
      fetchAllUpcomingOrders();
    };

    if (deferLoad && Number(deferMs) > 0) {
      deferTimer = window.setTimeout(runFetch, Number(deferMs));
    } else {
      runFetch();
    }

    // Hard stop — tab must never stay on "טוען…" even if every promise hangs.
    safetyTimer = window.setTimeout(() => {
      if (!cancelled) {
        console.warn("[OrdersCalendarCard] loading safety timeout fired");
        setIsLoading(false);
      }
    }, LOADING_MAX_MS);

    return () => {
      cancelled = true;
      if (deferTimer) window.clearTimeout(deferTimer);
      if (safetyTimer) window.clearTimeout(safetyTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once on mount; month filters locally
  }, [readyToFetch]);

  const upcomingOrders = useMemo(() => {
    return allOrders
      .filter((order) => orderTouchesMonth(order, currentMonth))
      .sort((a, b) => {
        const aDate = a.date || parseDate(a.checkin);
        const bDate = b.date || parseDate(b.checkin);
        if (!aDate || !bDate) return 0;
        return aDate.valueOf() - bDate.valueOf();
      });
  }, [allOrders, currentMonth]);

  const handlePreviousMonth = () => {
    setCurrentMonth((prev) => prev.subtract(1, "month"));
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => prev.add(1, "month"));
  };

  const handleCurrentMonth = () => {
    setCurrentMonth(todayAnchor.startOf("month"));
  };

  const formatDate = (date) => {
    if (!date) return "";
    return date.format("DD/MM/YYYY");
  };

  const handleDayClick = (dayData) => {
    if (dayData.orders && dayData.orders.length > 0) {
      setSelectedDate(dayData);
      setIsModalOpen(true);
    }
  };

  const handleOrderClick = (order) => {
    const params = new URLSearchParams();
    if (order.id) {
      params.append("orderId", order.id);
    }
    if (order.clientName) {
      params.append("mainContact", order.clientName);
    }
    if (order.type === "package") {
      params.append("orderType", "package");
    } else if (order.type === "tour") {
      params.append("orderType", "organized_tour");
    }
    navigate(ordersDashboardUrlFromSearchParams(params));
    setIsModalOpen(false);
  };

  const dayBuckets = useMemo(() => {
    const acc = {};
    const ensure = (dateKey) => {
      if (!acc[dateKey]) {
        acc[dateKey] = { checkIns: [], checkOuts: [], orders: [] };
      }
      return acc[dateKey];
    };

    upcomingOrders.forEach((order) => {
      const checkIn = order.date || parseDate(order.checkin);
      const checkOut = order.checkoutDate || parseDate(order.checkout);

      if (checkIn && checkIn.isSame(currentMonth, "month")) {
        const key = checkIn.format("YYYY-MM-DD");
        const bucket = ensure(key);
        bucket.checkIns.push(order);
        if (!bucket.orders.some((o) => o.id === order.id && o.type === order.type)) {
          bucket.orders.push(order);
        }
      }

      if (checkOut && checkOut.isSame(currentMonth, "month")) {
        const key = checkOut.format("YYYY-MM-DD");
        const bucket = ensure(key);
        bucket.checkOuts.push(order);
        if (!bucket.orders.some((o) => o.id === order.id && o.type === order.type)) {
          bucket.orders.push(order);
        }
      }
    });

    return acc;
  }, [upcomingOrders, currentMonth]);

  const today = todayAnchor;
  const isCurrentMonth =
    currentMonth.isSame(today, "month") && currentMonth.isSame(today, "year");
  const monthStart = currentMonth.startOf("month");
  const daysInMonth = currentMonth.endOf("month").date();
  const firstDayOfWeek = monthStart.day();

  const calendarDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const date = currentMonth.date(day);
      const dateKey = date.format("YYYY-MM-DD");
      const bucket = dayBuckets[dateKey] || { checkIns: [], checkOuts: [], orders: [] };
      days.push({
        day,
        date,
        dateKey,
        orders: bucket.orders,
        checkIns: bucket.checkIns,
        checkOuts: bucket.checkOuts,
      });
    }
    return days;
  }, [currentMonth, firstDayOfWeek, daysInMonth, dayBuckets]);

  const monthName = currentMonth.locale("he").format("MMMM YYYY");
  const eventsInMonth = Object.keys(dayBuckets).length;
  const emptyMonthHint =
    !isLoading &&
    !loadError &&
    allOrders.length > 0 &&
    eventsInMonth === 0
      ? `נטענו ${allOrders.length} הזמנות, אך אין כניסות/יציאות ב${monthName}`
      : !isLoading && !loadError && allOrders.length === 0
        ? "אין הזמנות להצגה ביומן"
        : null;

  const calendarOrderNeedsAttention = (o) => {
    if (o.type === "hotel") return !o.paid;
    if (o.rawLdsOrder) return !isLdsOrderBusinessSettled(o.rawLdsOrder);
    return !o.paid;
  };

  const getEventChips = (dayData) => {
    const checkInCount = dayData.checkIns?.length || 0;
    const checkOutCount = dayData.checkOuts?.length || 0;
    const orders = dayData.orders || [];
    const pending = orders.filter((o) => calendarOrderNeedsAttention(o)).length;
    const urgent = orders.filter(
      (o) => calendarOrderNeedsAttention(o) && o.date?.isBefore(today.add(3, "days"))
    ).length;

    const chips = [];
    if (checkInCount > 0) {
      chips.push({ label: `כניסות · ${checkInCount}`, color: "green", type: "checkin" });
    }
    if (checkOutCount > 0) {
      chips.push({ label: `יציאות · ${checkOutCount}`, color: "blue", type: "checkout" });
    }
    if (pending > 0) {
      chips.push({ label: `ממתין · ${pending}`, color: "orange", type: "pending" });
    }
    if (urgent > 0) {
      chips.push({ label: `דחוף · ${urgent}`, color: "red", type: "urgent" });
    }
    return chips;
  };

  return (
    <div className="orders-calendar-card" dir="rtl">
      <div className="orders-calendar-card__header">
        <div className="orders-calendar-card__header-main">
          <h2 className="orders-calendar-card__title">
            <HiOutlineCalendar className="orders-calendar-card__title-icon" />
            יומן הזמנות חודשי
          </h2>
        </div>
        <div className="orders-calendar-card__toolbar" dir="ltr">
          {!isCurrentMonth && (
            <button
              type="button"
              className="orders-calendar-card__toolbar-btn orders-calendar-card__toolbar-btn--today"
              onClick={handleCurrentMonth}
            >
              היום
            </button>
          )}
          <button
            type="button"
            className="orders-calendar-card__toolbar-btn orders-calendar-card__toolbar-btn--nav"
            onClick={handleNextMonth}
            aria-label="חודש הבא"
          >
            <HiOutlineChevronLeft aria-hidden />
          </button>
          <span className="orders-calendar-card__month-name" dir="rtl">
            {monthName}
          </span>
          <button
            type="button"
            className="orders-calendar-card__toolbar-btn orders-calendar-card__toolbar-btn--nav"
            onClick={handlePreviousMonth}
            aria-label="חודש קודם"
          >
            <HiOutlineChevronRight aria-hidden />
          </button>
        </div>
      </div>

      <div className="orders-calendar-card__body">
        {isLoading ? (
          <div className="orders-calendar-card__loading orders-calendar-card__loading--inline">
            טוען יומן הזמנות…
          </div>
        ) : null}
        {loadError ? (
          <div className="orders-calendar-card__status orders-calendar-card__status--error" role="alert">
            {loadError}
          </div>
        ) : null}
        {emptyMonthHint ? (
          <div className="orders-calendar-card__status" role="status">
            {emptyMonthHint}
            {allOrders.length > 0 ? (
              <button
                type="button"
                className="orders-calendar-card__status-action"
                onClick={() => {
                  const best = pickBestMonthWithEvents(allOrders, todayAnchor);
                  setCurrentMonth(best);
                }}
              >
                עבור לחודש עם הזמנות
              </button>
            ) : null}
          </div>
        ) : null}
        {fetchWarnings.length > 0 && !loadError ? (
          <div className="orders-calendar-card__status orders-calendar-card__status--warn" role="status">
            חלק ממקורות היומן נכשלו ({formatSourceWarningsHe(fetchWarnings)}).
            מוצגות ההזמנות שכן נטענו.
          </div>
        ) : null}
        <div
          className={`orders-calendar-card__calendar${
            isLoading ? " orders-calendar-card__calendar--refreshing" : ""
          }`}
        >
          <div className="orders-calendar-card__weekdays">
            {["א'", "ב'", "ג'", "ד'", "ה'", "ו'", "ש'"].map((day) => (
              <div key={day} className="orders-calendar-card__weekday">
                {day}
              </div>
            ))}
          </div>
          <div className="orders-calendar-card__days">
            {calendarDays.map((dayData, index) => {
              if (!dayData) {
                return (
                  <div key={`empty-${index}`} className="orders-calendar-card__day orders-calendar-card__day--empty" />
                );
              }

              const { day, date, orders } = dayData;
              const isToday = date.isSame(today, "day");
              const isPast = date.isBefore(today, "day");
              const eventChips = getEventChips(dayData);
              const hasEvents =
                orders.length > 0 ||
                (dayData.checkIns?.length || 0) > 0 ||
                (dayData.checkOuts?.length || 0) > 0;

              return (
                <div
                  key={`day-${date.format("YYYY-MM-DD")}`}
                  className={`orders-calendar-card__day ${
                    isToday ? "orders-calendar-card__day--today" : ""
                  } ${isPast ? "orders-calendar-card__day--past" : ""} ${
                    hasEvents ? "orders-calendar-card__day--has-events" : ""
                  }`}
                  onClick={() => handleDayClick(dayData)}
                  style={{ cursor: hasEvents ? "pointer" : "default" }}
                >
                  <div className="orders-calendar-card__day-number">{day}</div>
                  {eventChips.length > 0 && (
                    <div className="orders-calendar-card__day-events">
                      {eventChips.slice(0, 2).map((chip, chipIndex) => (
                        <span
                          key={chipIndex}
                          className={`orders-calendar-card__event-chip orders-calendar-card__event-chip--${chip.color}`}
                        >
                          {chip.label}
                        </span>
                      ))}
                      {eventChips.length > 2 && (
                        <span className="orders-calendar-card__event-more">
                          +{eventChips.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <Modal
        rootClassName="orders-calendar-card-modal"
        title={
          <div style={{ direction: "rtl", textAlign: "right" }}>
            <div style={{ fontSize: "18px", fontWeight: 600, marginBottom: "4px" }}>
              הזמנות לתאריך {selectedDate ? formatDate(selectedDate.date) : ""}
            </div>
            {selectedDate && selectedDate.orders.length > 0 && (
              <div style={{ fontSize: "14px", color: "#6b7280", fontWeight: 400 }}>
                סה"כ {selectedDate.orders.length} {selectedDate.orders.length === 1 ? "הזמנה" : "הזמנות"}
              </div>
            )}
          </div>
        }
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedDate(null);
        }}
        footer={null}
        width={800}
        style={{ direction: "rtl" }}
      >
        {selectedDate && selectedDate.orders.length > 0 ? (
          <div className="orders-calendar-card__modal-list">
            {selectedDate.orders.map((order, orderIndex) => {
              const ldsRow = order.rawLdsOrder;
              const ldsSettled = ldsRow ? isLdsOrderBusinessSettled(ldsRow) : false;
              const showPaidHighlight =
                order.type === "hotel" ? !!order.paid : ldsSettled;
              const modalOrderKey = `modal-order-${order.type || "unknown"}-${
                order.id || "no-id"
              }-${orderIndex}`;
              return (
              <div
                key={modalOrderKey}
                className={`orders-calendar-card__modal-item ${
                  showPaidHighlight ? "orders-calendar-card__modal-item--paid" : ""
                }`}
                onClick={() => handleOrderClick(order)}
              >
                <div className="orders-calendar-card__modal-header">
                  <span className="orders-calendar-card__modal-type">{order.typeLabel}</span>
                  {order.type === "hotel" ? (
                    order.paid ? (
                      <span className="orders-calendar-card__modal-paid-badge">שולם</span>
                    ) : null
                  ) : ldsRow ? (
                    <span
                      className={`orders-calendar-card__modal-paid-badge${
                        ldsSettled ? "" : " orders-calendar-card__modal-paid-badge--neutral"
                      }`}
                    >
                      {getLdsOrderBusinessStatusLabelHe(
                        deriveLdsOrderBusinessStatus(ldsRow),
                        "compact"
                      )}
                    </span>
                  ) : order.paid ? (
                    <span className="orders-calendar-card__modal-paid-badge">שולם</span>
                  ) : null}
                </div>
                <div className="orders-calendar-card__modal-content">
                  <div className="orders-calendar-card__modal-row">
                    <span className="orders-calendar-card__modal-label">לקוח:</span>
                    <span className="orders-calendar-card__modal-value">{order.clientName || "-"}</span>
                  </div>
                  <div className="orders-calendar-card__modal-row">
                    <span className="orders-calendar-card__modal-label">מלון/חבילה:</span>
                    <span className="orders-calendar-card__modal-value">{order.title}</span>
                  </div>
                  <div className="orders-calendar-card__modal-row">
                    <span className="orders-calendar-card__modal-label">יציאה:</span>
                    <span className="orders-calendar-card__modal-value">
                      {formatDate(order.date) || order.checkin || "-"}
                    </span>
                  </div>
                  {order.checkout && (
                    <div className="orders-calendar-card__modal-row">
                      <span className="orders-calendar-card__modal-label">חזרה:</span>
                      <span className="orders-calendar-card__modal-value">
                        {String(order.checkout).includes("/")
                          ? order.checkout
                          : formatDate(order.checkoutDate || parseDate(order.checkout))}
                      </span>
                    </div>
                  )}
                </div>
                <div className="orders-calendar-card__modal-footer">
                  <button
                    type="button"
                    className="orders-calendar-card__modal-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOrderClick(order);
                    }}
                  >
                    <FiExternalLink />
                    צפה בהזמנה
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        ) : (
          <div className="orders-calendar-card__modal-empty">אין הזמנות לתאריך זה</div>
        )}
      </Modal>
    </div>
  );
};

export default OrdersCalendarCard;
